import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '../../app/layouts/AuthLayout.js';
import { PageHeader } from '../../ui/components/PageHeader.js';
import '../../ui/components/button.css';
import './sign-in.css';

type State = 'ready' | 'working' | 'done' | 'invalid' | 'failed';

/** The token from the link's fragment, read once and removed from the address bar and history. */
function takeToken(): string | undefined {
  const token = decodeURIComponent(window.location.hash.replace(/^#/, ''));
  if (!/^[A-Za-z0-9_-]{40,100}$/.test(token)) return undefined;
  window.history.replaceState(window.history.state, '', '/auth/not-me');
  return token;
}

let taken: { token: string | undefined } | undefined;

/**
 * "This wasn't me" (`/auth/not-me#token`), from the email sent when an
 * account signs in on a new device. One press ends every session of the
 * account, including the one that should not exist. The token is single
 * use and lives only in the fragment, so it never reaches a server log. A
 * plain `fetch`, so the public bundle stays small.
 */
export function NotMePage() {
  const { t } = useTranslation();
  const [token] = useState(() => {
    taken ??= { token: takeToken() };
    return taken.token;
  });
  const [state, setState] = useState<State>(token ? 'ready' : 'invalid');

  const endEverything = async () => {
    if (!token) return;
    setState('working');
    try {
      const response = await fetch('/api/v1/auth/not-me', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token }),
      });
      setState(response.ok ? 'done' : response.status === 422 ? 'invalid' : 'failed');
    } catch {
      setState('failed');
    }
  };

  return (
    <AuthLayout>
      <div className="sign-in">
        <PageHeader title={t('notMe.title')} />
        {state === 'done' ? (
          <div className="sign-in__form" role="status">
            <h2 className="sign-in__step-title">{t('notMe.doneTitle')}</h2>
            <p>{t('notMe.doneBody')}</p>
            <a className="btn btn--primary sign-in__submit" href="/sign-in">
              {t('verify.signInAgain')}
            </a>
          </div>
        ) : state === 'invalid' ? (
          <div className="sign-in__form">
            <p role="alert">{t('notMe.invalid')}</p>
            <a className="btn btn--secondary sign-in__submit" href="/sign-in">
              {t('verify.signInAgain')}
            </a>
          </div>
        ) : (
          <div className="sign-in__form">
            <p>{t('notMe.body')}</p>
            {state === 'failed' ? <p role="alert">{t('notMe.failed')}</p> : null}
            <button
              className="btn btn--danger sign-in__submit"
              type="button"
              disabled={state === 'working'}
              onClick={() => void endEverything()}
            >
              {t('notMe.action')}
            </button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
